/**
 * @file Provides authentication functionality via a React context provider.
 */

import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useMemo, useState } from 'react'

/**
 * @property {boolean} isAuthenticated - Flag to indicate whether the user is authenticated or not.
 * @property {Dispatch<SetStateAction<boolean>>} setIsAuthenticated - Function to set the isAuthenticated state.
 * @property {function} handleLogout - Function to handle user logout.
 */
type TAuthContextProps = {
  isAuthenticated: boolean
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>
  isUnauthorised?: boolean
  setIsUnauthorised?: Dispatch<SetStateAction<boolean>>
  handleLogout: () => void
}

/**
 * The AuthContext provides the authentication-related data and functionalities.
 */
const AuthContext = createContext<TAuthContextProps | undefined>(undefined)

type TAuthProviderProps = {
  children: ReactNode
}

/**
 * AuthProvider component to provide authentication functionalities.
 * @param {TAuthProviderProps} props - The props for the AuthProvider component.
 * @property {ReactNode} children - The children components to wrap with this provider.
 */
export const AuthProvider = ({ children }: TAuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isUnauthorised, setIsUnauthorised] = useState(false)

  /**
   * Log the user out by setting `isAuthenticated` to false.
   * TODO - Add logout API call.
   */
  const handleLogout = () => {
    setIsAuthenticated(false)
  }

  const contextValue = useMemo<TAuthContextProps>(
    () => ({
      isAuthenticated,
      setIsAuthenticated,
      isUnauthorised,
      setIsUnauthorised,
      handleLogout,
    }),
    [isAuthenticated, isUnauthorised],
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

/**
 * Custom hook to use the AuthContext.
 * @returns {TAuthContextProps} The Auth context values.
 */
export const useAuthContext = (): TAuthContextProps => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuthContext must be used within AuthProvider')
  }

  return context
}
